/**
 * Gemeinsame Teile der Morse-Seite: Daten vom Server, kleine Bauhelfer, die „Bühne“ jeder Übung und die
 * Anzeige „Zeichen für Zeichen“. (06.10.2026, Morse-Überarbeitung)
 *
 * Die Bühne ist für Hören und Geben DIESELBE: oben der Zustand in Worten UND Form (Bereit ○, Läuft ●,
 * Pause ❚❚, Ausgewertet ✓, Kein Ton △ — die Form zeichnet style.css, nie nur eine Farbe), darunter EIN großer
 * Hauptknopf, der immer an derselben Stelle sitzt: „Start“ ↔ „Stopp“, daneben während der Übung
 * „Pause“/„Weiter“. Darunter die Statuszeile (role=status) — dort, wo man hinschaut, nicht am Seitenende.
 *
 * ⚠️ Keine Inline-Styles: Zustände stehen als data-Attribute am Element, das Aussehen in style.css (`.morse-…`).
 */
import { KOCH_SCHWELLE, type Auswertung, type StufenAufgabe, type Umfang } from "../../shared/morse-koch.js";
import { MORSE_TABELLE, type MorseEinstellungen, type ZeitPlan } from "../../shared/morse.js";
import { musterBild, musterSprechen, wenigBewegung } from "./morse-bild.js";
import type { HoerspurDienst } from "./morse-hoerspur-ansicht.js";
import type { DiktatAnschluss } from "./morse-sprechen.js";
import type { MorseSpieler, MorseSpielErgebnis } from "./morse-ton.js";
import type { RundenVerlauf } from "./morse-balken.js";

// Die Muster-Helfer wohnen in morse-bild.ts — hier weitergereicht, damit die Reiter sie wie bisher finden.
export { musterSprechen, musterZeigen, wenigBewegung } from "./morse-bild.js";

/* ── Daten vom Server ─────────────────────────────────────────────── */

export interface ZeichenStand {
  zeichen: string;
  gehoert: number;
  richtig: number;
  guete: number;
  zuletzt: string;
}
/** Wie eine Übung gebucht wird: Hören (getippt), Geben (Taste), Sprechen („Augen zu“: gehört, gesprochen beantwortet). */
export type MorseBuchungsArt = "hoeren" | "geben" | "sprechen";
export interface Sitzung {
  id: number;
  zeitpunkt: string;
  art: MorseBuchungsArt;
  stufe: number;
  richtig: number;
  gesamt: number;
  zuviel: number;
  dauerMs: number;
}
export interface MorseStandDaten {
  stufe: number;
  /** Die höchste Stufe, die das Konto erreicht hat (ältere Server schicken sie nicht — dann gilt `stufe`). */
  hoechsteStufe?: number;
  einstellungen: MorseEinstellungen;
  zeichen: ZeichenStand[];
  sitzungen: Sitzung[];
  heute: { sitzungen: number; minuten: number };
  /** Das Fenster der Stufe (16.10.2026; ältere Server schicken es nicht — dann zeigt die Seite kein Ziel). */
  fenster?: StufenAufgabe[];
}
export interface BuchungsAntwort {
  stufe: number;
  stufeVorher: number;
  aufgestiegen: boolean;
  stand: MorseStandDaten;
}
export type Buchung = { ok: true; daten: BuchungsAntwort } | { ok: false; fehler: string };

export type MorseReiter = "hoeren" | "geben" | "tabelle" | "fortschritt";

/** Sobald ein Plan auf der Uhr liegt: der Plan und wann sein erster Ton beginnt (für ein Bild, das mitleuchtet). */
export type BeimStart = (plan: ZeitPlan, inMs: number) => void;

/** Das Ergebnis, die Übungsstufe zu setzen: gesetzt (der Stand der Seite folgt schon) — oder ein Grund in Worten. */
export type StufeErgebnis = { ok: true } | { ok: false; fehler: string };

/** Was „Eine Stufe zurück“ / „Stufe wählen“ (morse-stufe.ts) von der Seite braucht. */
export interface StufeAnschluss {
  /** Die Übungsstufe setzen (Server) — der Stand der Seite folgt der Antwort. */
  setze(stufe: number): Promise<StufeErgebnis>;
  /** Warum man die Stufe JETZT nicht ändern kann (eine Runde läuft oder ruht) — oder `null`. */
  sperre(): string | null;
}

/** Die höchste erreichte Stufe eines Stands (nie unter der aktuellen). */
export function hoechsteStufeVon(s: Pick<MorseStandDaten, "stufe" | "hoechsteStufe">): number {
  return Math.max(s.stufe, s.hoechsteStufe ?? s.stufe);
}

/** Was ein Reiter von der Seite braucht (morse-view.ts stellt es). */
export interface MorseUmgebung {
  stand(): MorseStandDaten;
  spieler: MorseSpieler;
  jetzt(): number;
  zufall(): number;
  /** Eine Diagnosezeile „[morse] …“ ans Server-Protokoll — Zustände und Zahlen, NIE Inhalte. */
  spur(text: string): void;
  /** Einen Text mit den Einstellungen spielen (`langsam`: Fehler noch einmal, 60 % Tempo). */
  spiele(text: string, langsam?: boolean, beimStart?: BeimStart): Promise<MorseSpielErgebnis>;
  /** Die Tabelle öffnen (Schnellzugriff aus der Bühne); eine laufende Übung pausiert, „Zurück“ führt hierher. */
  zeigeTabelle?(): void;
  /** Das Tempo der Stufe nehmen — derselbe Weg wie unter „Tempo und Ton“ (nur auf einen Tipp hin). */
  vorgabeNehmen?(): void;
  /** Die Übungsstufe ändern (Fortschritt, und nach einem Aufstieg beim Hören) — morse-stufe.ts. */
  stufe?: StufeAnschluss;
  /**
   * Die Hörspur (morse-hoerspur-ansicht.ts): Plätze für ihren Knopf, der Abspieler für die Anzeige der Tabelle,
   * und `stille(grund)` — wer Ton braucht (eine Übung beginnt, die Taste, die Ordnung), ruft es zuerst.
   */
  hoerspur?: HoerspurDienst;
  /**
   * Eine Übung buchen; der Stand der Seite folgt der Antwort. `kennung`: dieselbe Buchung bucht der Server nur
   * einmal. `keepalive`: die Seite geht gerade — die Anfrage soll das überleben.
   */
  buche(art: MorseBuchungsArt, aufgaben: Array<{ soll: string; ist: string }>, dauerMs: number, o?: { kennung?: string; keepalive?: boolean; umfang?: Umfang }): Promise<Buchung>;
  /** Läuft oder ruht gerade eine Runde (Hören oder Geben)? Dann ändert sich „Üben mit“ nicht (morse-umfang.ts). */
  rundeOffen?(): boolean;
  /** Die Antwort einsprechen (Reiter Hören, morse-sprechen.ts): Mikrofon und Mitschrift. Fehlt er, gibt es den Knopf nicht. */
  sprechen?: DiktatAnschluss;
  /** „Augen zu“ (morse-augen-zu.ts): ein kurzer Quittungston — hell (richtig) oder tief (falsch) —, löst auf, wenn er verklungen ist. */
  quittung?(art: "richtig" | "falsch" | "weiter"): Promise<MorseSpielErgebnis>;
  /** „Augen zu“: eine kurze Stimme (≤ 300 Zeichen) über den vorhandenen TTS-Weg. Fehlt sie, fällt sie still weg. */
  stimme?(text: string): StimmeLauf;
  /** Eine Stufe geschafft (16.10.2026): Karte und kurze Tonfolge (morse-view.ts) — erst nach dem Ende der Runde rufen. */
  feiere?(d: BuchungsAntwort): void;
  /** „Augen zu“: eine Messzeile `[morse-sprechen] …` ins Journal — Zahlen und feste Stichwörter, nie Audio; Gesagtes nur als `wort=` (≤ 12 Buchstaben, kein Satz). */
  messe?(zeile: string): void;
}

/** Eine laufende Stimme: `fertig` löst auf, wenn sie verklungen (oder gescheitert) ist; `stopp` bricht sie ab. */
export interface StimmeLauf {
  fertig: Promise<void>;
  stopp(): void;
}

/** Ein Reiter mit Übung (Hören, Geben). */
export interface MorseUebung {
  readonly laeuft: boolean;
  readonly pausiert: boolean;
  /** Leertaste außerhalb von Bedienelementen; gibt zurück, ob sie verbraucht wurde. */
  leertaste(art: "keydown" | "keyup", wiederholt: boolean): boolean;
  stopp(grund: string): void;
  /**
   * Seite verdeckt, Reiter gewechselt: eine laufende Übung pausiert, ein gehaltener Druck wird verworfen, und was
   * beantwortet ist, wird gebucht (`gehtWeg`: die Seite verschwindet vielleicht ganz — keepalive).
   */
  ruhe(grund: string, gehtWeg?: boolean): void;
  /** Das Fenster verliert den Fokus: eine gehaltene Taste los (Ton und Miete), sonst nichts. */
  loeseTaste?(): void;
  /** Der Stand hat sich geändert (Aufstieg, Einstellungen). */
  standGeaendert(): void;
  /** Endgültig weg: alles aus, nichts buchen. */
  zerstoere(): void;
}

/** Was „kein Ton“ heißt — je nach Zustand des Kontexts, in Worten (nie stilles Nichts). `wo`: worauf man noch einmal tippt. */
export function keinTonText(zustand: string, wo = "auf Start"): string {
  if (zustand === "fehlt") return "Dieses Gerät spielt hier keinen Ton ab (Web Audio fehlt).";
  if (zustand === "interrupted")
    return `Der Ton ist gerade belegt — ein Anruf, eine Aufnahme oder eine andere App. Beende das und tipp noch einmal ${wo}.`;
  return `Kein Ton: das Gerät hat die Wiedergabe nicht freigegeben. Tipp noch einmal ${wo}. Am iPhone auch Lautstärke und Stummschalter prüfen.`;
}

/** „hinweis“: kein Fehler, aber etwas, das man lesen muss (eine Aufnahme geht vor) — als Kasten in normaler Schrift. */
export type MeldungsArt = "gut" | "fehler" | "info" | "hinweis";

/** Eine Statuszeile (role=status), anfangs leer und ausgeblendet. */
export function baueMeldung(): HTMLElement {
  const m = el("p", "morse-meldung");
  m.setAttribute("role", "status");
  m.hidden = true;
  return m;
}

/** In eine Statuszeile schreiben — leerer Text blendet sie aus. Die Art steht am Element (Aussehen: style.css). */
export function meldeIn(ziel: HTMLElement, text: string, art: MeldungsArt = "info"): void {
  ziel.textContent = text;
  ziel.dataset.art = art;
  ziel.hidden = !text;
}

/**
 * Etwas zum Anhören spielen (ein Zeichen, das Ziel, die Probe) — kam kein Ton, steht es in Worten in `melde` und
 * als Diagnosezeile im Protokoll (`wo` nennt die Stelle, ohne Inhalt).
 */
export function hoereMitMeldung(
  u: Pick<MorseUmgebung, "spiele" | "spieler" | "spur">,
  text: string,
  melde: (text: string) => void,
  wo: string,
  langsam = false,
  beimStart?: BeimStart,
): Promise<MorseSpielErgebnis> {
  return u.spiele(text, langsam, beimStart).then((e) => {
    if (e === "kein-ton") {
      const zustand = u.spieler.zustand();
      u.spur(`kein-ton ${wo} zustand=${zustand}`);
      melde(keinTonText(zustand, "darauf"));
    }
    return e;
  });
}

/* ── kleine Helfer ────────────────────────────────────────────────── */

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (klasse) e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

export function knopf(text: string, klasse: string, beiKlick: () => void): HTMLButtonElement {
  const b = el("button", klasse, text);
  b.type = "button";
  b.addEventListener("click", beiKlick);
  return b;
}

/**
 * Ein dezenter Balken für die Güte (0–1): ab der Koch-Schwelle gut, unter 60 % schwach. Nur Bild (aria-hidden) —
 * die Zahl steht immer in Worten daneben. Stand, Fortschritt und Tabelle zeichnen ihn gleich.
 */
export function gueteBalken(klasse: string, wert: number): HTMLMeterElement {
  const m = el("meter", klasse);
  m.min = 0;
  m.max = 1;
  m.low = 0.6;
  m.high = KOCH_SCHWELLE;
  m.optimum = 1;
  m.value = wert;
  m.setAttribute("aria-hidden", "true");
  return m;
}

/** „92 %“ — mit festem Leerzeichen, damit Zahl und Zeichen nie auf zwei Zeilen landen. */
export function prozent(x: number): string {
  return `${Math.round(x * 100)}\u00a0%`;
}

/** Die Bühne ins Bild holen, falls sie unten aus dem Fenster ragt (Telefon: Kopf, Stand und Reiter stehen davor). */
export function insBild(e: HTMLElement): void {
  try {
    e.scrollIntoView({ block: "nearest", behavior: wenigBewegung() ? "auto" : "smooth" });
  } catch {
    /* ältere Browser */
  }
}

/** Wörter pro Minute aus einer Punktlänge (PARIS). */
export function wpmAus(punkt: number): number {
  return Math.round(1200 / punkt);
}

/** „1:42 Min.“ / „40 s“ */
export function dauerText(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")} Min.`;
}

/**
 * Ein Zeichen als Knopf (Zeichen groß, Muster klein) — antippen spielt es. `neu` markiert das neue Zeichen der
 * Stufe (Rahmen UND das Wort „neu“, nicht nur Farbe).
 */
export function zeichenKnopf(z: string, beiKlick: () => void, o: { neu?: boolean; zusatz?: string; leise?: boolean } = {}): HTMLButtonElement {
  const m = MORSE_TABELLE[z] ?? "";
  const b = el("button", "morse-zeichen");
  b.type = "button";
  if (o.neu) b.dataset.neu = "true";
  // „Alle Zeichen“: was noch nie gehört wurde, steht leise abgesetzt da — und sagt es auch in Worten.
  if (o.leise) b.dataset.leise = "true";
  b.setAttribute(
    "aria-label",
    `${z} anhören: ${musterSprechen(m)}${o.neu ? ", neu auf dieser Stufe" : ""}${o.leise ? ", noch nie gehört" : ""}${o.zusatz ? `, ${o.zusatz}` : ""}`,
  );
  b.append(el("span", "morse-zeichen-name", z), musterBild(m, "s"));
  if (o.neu) b.append(el("span", "morse-zeichen-marke", "neu"));
  if (o.zusatz) b.append(el("span", "morse-zeichen-marke", o.zusatz));
  b.addEventListener("click", beiKlick);
  return b;
}

/** Wahl aus zwei, drei Möglichkeiten (Gruppen/Wörter …): Knöpfe mit aria-pressed in einer Wanne. */
export function wahl<T extends string>(
  titel: string,
  moeglich: ReadonlyArray<{ id: T; text: string }>,
  beiWahl: (id: T) => void,
): { wurzel: HTMLElement; setze: (aktiv: T, aus?: ReadonlyMap<T, string>, gesperrt?: boolean) => void } {
  const wurzel = el("div", "morse-wahl");
  wurzel.setAttribute("role", "group");
  wurzel.setAttribute("aria-label", titel);
  const knoepfe = new Map<T, HTMLButtonElement>();
  for (const m of moeglich) {
    const b = knopf(m.text, "morse-wahl-knopf", () => beiWahl(m.id));
    knoepfe.set(m.id, b);
    wurzel.appendChild(b);
  }
  return {
    wurzel,
    setze(aktiv, aus = new Map(), gesperrt = false) {
      for (const m of moeglich) {
        const b = knoepfe.get(m.id)!;
        const grund = aus.get(m.id);
        b.setAttribute("aria-pressed", String(m.id === aktiv));
        b.dataset.active = String(m.id === aktiv);
        b.disabled = gesperrt || grund !== undefined;
        b.textContent = grund ? `${m.text} (${grund})` : m.text;
      }
    },
  };
}

/* ── Die Bühne ────────────────────────────────────────────────────── */

export type BuehnenZustand = "bereit" | "laeuft" | "pause" | "fertig" | "fehler";

const ZUSTAND_WORT: Readonly<Record<BuehnenZustand, string>> = {
  bereit: "Bereit",
  laeuft: "Läuft",
  pause: "Pause",
  fertig: "Ausgewertet",
  fehler: "Kein Ton",
};

export interface Buehne {
  wurzel: HTMLElement;
  /** Die variable Fläche (je Zustand andere Teile). */
  flaeche: HTMLElement;
  /** Die Zeile des Hauptknopfs: dort steht neben „Start“ auch Nachrangiges (die Vorführung, 08.10.2026). */
  steuer: HTMLElement;
  haupt: HTMLButtonElement;
  neben: HTMLButtonElement;
  /** Statuszeile (role=status); leerer Text blendet sie aus. */
  melde: (text: string, art?: MeldungsArt) => void;
  /** Zustand in Wort und Form, dazu der Zähler („Gruppe 3 von 10“). */
  setze: (z: BuehnenZustand, o?: { wort?: string; zaehler?: string }) => void;
  zaehler: (text: string) => void;
  /** Der Verlauf der Runde als Strichleiste unter dem Kopf (morse-balken.ts) — `null` blendet ihn aus. */
  verlauf: (v: RundenVerlauf | null) => void;
}

/**
 * Die Bühne einer Übung. `beiHaupt` bekommt die Rolle des Knopfs im Moment des Klicks („start“/„stopp“),
 * `beiNeben` „pause“/„weiter“. `zurTabelle`: der Schnellzugriff „Tabelle“ oben rechts — aus der Übung mit EINEM
 * Tipp (06.10.2026, Martin: „Was mir fehlt, ist … auch eine Tabelle“).
 */
export function baueBuehne(
  name: string,
  beiHaupt: (rolle: "start" | "stopp") => void,
  beiNeben: (rolle: "pause" | "weiter") => void,
  zurTabelle?: () => void,
): Buehne {
  const wurzel = el("section", "morse-buehne");
  wurzel.setAttribute("aria-label", name);
  const kopf = el("div", "morse-buehne-kopf");
  const zustand = el("p", "morse-zustand");
  const zeichen = el("span", "morse-zustand-form");
  zeichen.setAttribute("aria-hidden", "true");
  const wort = el("span", "morse-zustand-wort");
  zustand.append(zeichen, wort);
  const zaehler = el("p", "morse-zaehler");
  kopf.append(zustand, zaehler);
  if (zurTabelle) {
    const t = knopf("", "morse-zur-tabelle", zurTabelle);
    const form = el("span", "morse-zur-tabelle-form");
    form.setAttribute("aria-hidden", "true");
    t.append(form, el("span", "morse-zur-tabelle-text", "Tabelle"));
    t.setAttribute("aria-label", "Morse-Tabelle öffnen");
    kopf.appendChild(t);
  }

  // 23.10.2026: der Verlauf der Runde — ein Strich je Aufgabe (richtig voll, falsch kurz mit Kerbe, jetzt umrandet).
  const verlauf = el("div", "morse-verlauf");
  verlauf.setAttribute("role", "img");
  verlauf.hidden = true;

  const steuer = el("div", "morse-steuer");
  const haupt = el("button", "morse-haupt");
  haupt.type = "button";
  haupt.setAttribute("aria-keyshortcuts", "Space");
  const hauptForm = el("span", "morse-haupt-form");
  hauptForm.setAttribute("aria-hidden", "true");
  const hauptText = el("span", "morse-haupt-text");
  haupt.append(hauptForm, hauptText);
  /*
   * ⚠️ Prüfbefund K7: vorher sperrte ein Prellschutz BEIDE Knöpfe 400 ms nach jedem Wechsel — ein Stopp oder eine
   *    Pause gleich nach dem Start ging verloren. Jetzt zählt jeder einzelne Tipp sofort; nur der ZWEITE Klick EINES
   *    Doppelklicks (`detail` 2) auf denselben Knopf zählt nicht — sonst hielte ein Doppeltipp auf „Start“ die
   *    Runde im selben Atemzug wieder an. Pause/Weiter und Esc sind nie gesperrt.
   */
  haupt.addEventListener("click", (ev) => {
    if (ev.detail > 1) return;
    beiHaupt(haupt.dataset.rolle === "stopp" ? "stopp" : "start");
  });
  const neben = el("button", "morse-neben");
  neben.type = "button";
  const nebenForm = el("span", "morse-neben-form");
  nebenForm.setAttribute("aria-hidden", "true");
  const nebenText = el("span", "morse-neben-text");
  neben.append(nebenForm, nebenText);
  neben.addEventListener("click", () => beiNeben(neben.dataset.rolle === "weiter" ? "weiter" : "pause"));
  steuer.append(haupt, neben);

  const meldung = baueMeldung();
  const flaeche = el("div", "morse-flaeche");
  wurzel.append(kopf, verlauf, steuer, meldung, flaeche);

  const setzeZaehler = (text: string): void => {
    if (zaehler.textContent !== text) zaehler.textContent = text;
  };
  const b: Buehne = {
    wurzel,
    flaeche,
    steuer,
    haupt,
    neben,
    melde: (text, art = "info") => meldeIn(meldung, text, art),
    setze(z, o = {}) {
      wurzel.dataset.zustand = z;
      zustand.dataset.zustand = z;
      wort.textContent = o.wort ?? ZUSTAND_WORT[z];
      const stopp = z === "laeuft" || z === "pause";
      haupt.dataset.rolle = stopp ? "stopp" : "start";
      // Zwei Zustände, sonst nichts (Martin: „Start und Stopp“) — auch nach der Auswertung heißt er „Start“.
      hauptText.textContent = stopp ? "Stopp" : "Start";
      neben.hidden = !stopp;
      neben.dataset.rolle = z === "pause" ? "weiter" : "pause";
      nebenText.textContent = z === "pause" ? "Weiter" : "Pause";
      if (o.zaehler !== undefined) setzeZaehler(o.zaehler);
    },
    zaehler: setzeZaehler,
    verlauf(v) {
      verlauf.hidden = !v || v.striche.length === 0;
      if (!v) return;
      verlauf.setAttribute("aria-label", v.text);
      while (verlauf.children.length > v.striche.length) verlauf.lastElementChild?.remove();
      while (verlauf.children.length < v.striche.length) verlauf.appendChild(el("span", "morse-verlauf-strich"));
      v.striche.forEach((art, i) => {
        const s = verlauf.children[i] as HTMLElement;
        if (s.dataset.art !== art) s.dataset.art = art;
      });
      verlauf.dataset.fest = String(v.gesamt !== null);
    },
  };
  b.setze("bereit", { zaehler: "" });
  return b;
}

/**
 * Nach dem Ende einer Runde gehört der Fokus dem Startknopf (Prüfbefund W3): sonst blieb er auf der Taste oder im
 * Feld, und die Leertaste morste frei bzw. tat nichts, statt — wie angesagt — die nächste Runde zu starten.
 * Nur, wenn der Fokus in der Bühne oder nirgends war; anderswo wird er nicht weggenommen.
 */
export function fokusAufStart(b: Buehne): void {
  const a = document.activeElement;
  if (!a || a === document.body || b.wurzel.contains(a)) b.haupt.focus({ preventScroll: true });
}

/* ── Zeichen für Zeichen ──────────────────────────────────────────── */

/**
 * Soll gegen Ist, Zeichen für Zeichen. Falsch heißt: Fläche UND darunter, was stattdessen kam („∅“ für
 * ausgelassen — ein Strich sähe aus wie Morse, Prüfbefund K9). Die Gruppen der Vorlage bleiben getrennt.
 */
export function vergleich(soll: string, a: Auswertung): HTMLElement {
  const reihe = el("span", "morse-vergleich");
  let i = 0;
  for (const gruppe of soll.split(" ")) {
    const g = el("span", "morse-vergleich-gruppe");
    for (let j = 0; j < [...gruppe].length; j++) {
      const z = a.zeichen[i++];
      if (!z) continue;
      const s = el("span", "morse-vergleich-zeichen");
      s.dataset.richtig = String(z.richtig);
      s.append(el("span", "morse-vergleich-soll", z.zeichen), el("span", "morse-vergleich-statt", z.richtig ? "" : z.statt ?? "∅"));
      // Falsch: das richtige Muster gleich darunter (06.10.2026) — so sieht man, WIE es klingen sollte.
      if (!z.richtig) s.appendChild(musterBild(MORSE_TABELLE[z.zeichen] ?? "", "s"));
      const muster = MORSE_TABELLE[z.zeichen] ?? "";
      s.setAttribute(
        "aria-label",
        z.richtig ? `${z.zeichen} richtig` : `${z.zeichen} falsch, ${z.statt ? `du hattest ${z.statt}` : "ausgelassen"}; richtig ist ${musterSprechen(muster)}`,
      );
      g.appendChild(s);
    }
    reihe.appendChild(g);
  }
  return reihe;
}

/** Ist das Ziel ein Element, das die Leertaste selbst bedient (dann gehört sie ihm)? */
export function bedientLeertasteSelbst(ziel: EventTarget | null): boolean {
  if (!(ziel instanceof Element)) return false;
  if ((ziel as HTMLElement).isContentEditable) return true;
  return !!ziel.closest("input, textarea, select, button, a[href], summary, [role=tab], [role=button], [role=slider], [role=checkbox], [role=radio]");
}

/**
 * Eine Leiste, die in `ms` von voll auf leer läuft — über die Web Animations API, damit kein `style`-Attribut
 * entsteht. Gibt den Halt zurück. Bei reduzierter Bewegung in ganzen Sekunden.
 */
export function laufeLeiste(leiste: HTMLElement, ms: number): () => void {
  const fuellung = leiste.firstElementChild as HTMLElement | null;
  if (!fuellung || typeof fuellung.animate !== "function") return () => undefined;
  leiste.hidden = false;
  const schritte = Math.max(1, Math.round(ms / 1000));
  const anim = fuellung.animate([{ transform: "scaleX(1)" }, { transform: "scaleX(0)" }], {
    duration: Math.max(1, ms),
    easing: wenigBewegung() ? `steps(${schritte}, end)` : "linear",
    fill: "forwards",
  });
  return () => {
    try {
      anim.cancel();
    } catch {
      /* egal */
    }
    leiste.hidden = true;
  };
}

export function baueLeiste(klasse: string): HTMLElement {
  const leiste = el("div", `morse-leiste ${klasse}`);
  leiste.setAttribute("aria-hidden", "true");
  leiste.appendChild(el("span", "morse-leiste-fuellung"));
  leiste.hidden = true;
  return leiste;
}
